import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import './PulseStrip.css';

/* How much is happening here THIS WEEK — one full-width strip across the top of the company
   admin's tile grid.

   ★ Why it exists at all. Every tile under it reports a total, and a total is the right thing
   to lead with but it stands still: an admin who opens this screen every Monday sees the same
   grid while the work goes on underneath it. This is the one figure that moves every week, and
   the number beside it — how many DIFFERENT people were behind those things — is the one that
   answers the question an admin actually has. Thirty-eight things done by three people and
   thirty-eight done by fourteen are not the same company.

   ★ The sparkline measures EXACTLY what the headline measures: the same six sources, bucketed
   by week. A graph counting one thing under a total counting another is a strip arguing with
   itself, and nobody would ever catch it.

   ★ Renders nothing at all until there is something to show. A strip reading "0 things · 0
   people" over an empty grid is a company being told it is failing on its first day. */

const SPARK_W = 96;
const SPARK_H = 24;

/* The sources, in the order they read as a sentence: what people answered, what they said about
   each other, what they filled in, what they posted, what they finished, what we scored. */
const SOURCES = ['rsvp', 'cheer', 'survey', 'post', 'mission', 'result'];

const Sparkline = ({ weeks }) => {
  const values = (weeks || []).map((w) => Number(w.total) || 0);
  if (values.length < 2) return null;
  const top = Math.max(...values, 1);
  const step = SPARK_W / (values.length - 1);
  // Baseline at the bottom edge, so a zero week is a point on the floor rather than off it.
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(SPARK_H - (v / top) * (SPARK_H - 2) - 1).toFixed(1)}`)
    .join(' ');
  const last = points.split(' ').pop().split(',');
  return (
    <svg className="pulse__spark" viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
         width={SPARK_W} height={SPARK_H} aria-hidden="true" focusable="false">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.6"
                strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="2.2" fill="currentColor" />
    </svg>
  );
};

const PulseStrip = ({ activity }) => {
  const { t } = useTranslation();
  if (!activity) return null;
  const week = activity.this_week || {};
  const total = Number(week.total) || 0;
  const weeks = activity.weeks || [];
  // Everything at zero over the whole window means nothing has ever happened here — that is a
  // company still setting up, and it does not need a scoreboard saying so.
  if (!total && !weeks.some((w) => (Number(w.total) || 0) > 0)) return null;

  const prev = Number(activity.prev_week) || 0;
  const delta = total - prev;
  const bySource = week.by_source || {};
  const parts = SOURCES
    .filter((s) => (Number(bySource[s]) || 0) > 0)
    .map((s) => t(`pulseSource_${s}`, { count: Number(bySource[s]) }));

  return (
    <div className="stat-tile stat-tile--teal pulse">
      <div className="pulse__top">
        <span className="pulse__icon"><Icon name="bolt" /></span>
        <span className="pulse__head">
          <span className="pulse__label">{t('thisWeek')}</span>
          <span className="pulse__figures">
            <strong className="pulse__total">{total}</strong>
            <span className="pulse__things">{t('pulseThings', { count: total })}</span>
            <span className="pulse__dot">·</span>
            <span className="pulse__people">{t('pulsePeople', { count: week.people || 0 })}</span>
            {/* Only a real move draws an arrow. A week that landed exactly level says nothing,
                and "±0" beside a number is worse than the number alone. */}
            {delta !== 0 && (
              <span className={`pulse__delta pulse__delta--${delta > 0 ? 'up' : 'down'}`}>
                {delta > 0 ? '▲' : '▼'} {Math.abs(delta)}
              </span>
            )}
          </span>
        </span>
        <span className="pulse__sparkwrap" title={t('pulseSparkTitle', { n: weeks.length })}>
          <Sparkline weeks={weeks} />
        </span>
      </div>
      {parts.length > 0 && <div className="pulse__breakdown">{parts.join(' · ')}</div>}
    </div>
  );
};

export default PulseStrip;
